import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditContactSelectField } from '../../../EditContactSelectField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        parentContactId {
          id
          name
        }
        relationshipPriority
        relationship1
        relationship2
        relationshipStrength
        relationshipStrengthDescription
        serviceProviderFlag
        serviceProviderType
      }
    }
  }
`;

const RELATIONSHIPS_SERVICE_PROVIDER_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean; isNumber?: boolean }
> = {
  parentContactIdId: { apiField: 'parentContactId' },
  relationship1: { apiField: 'relationship1' },
  relationship2: { apiField: 'relationship2' },
  relationshipPriority: { apiField: 'relationshipPriority' },
  relationshipStrength: { apiField: 'relationshipStrength', isNumber: true },
  relationshipStrengthDescription: {
    apiField: 'relationshipStrengthDescription'
  },
  serviceProvider: { apiField: 'serviceProviderFlag', isBoolean: true },
  serviceProviderType: { apiField: 'serviceProviderType' }
};

export interface EditRelationshipsServiceProviderDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditRelationshipsServiceProviderDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditRelationshipsServiceProviderDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save relationships & service provider');
      } else {
        toast.success('Relationships & service provider saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving relationships & service provider:', error);
      toast.error('Failed to save relationships & service provider');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Relationships & Service Provider"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditContactSelectField
          excludeId={contactId}
          label="Parent Contact"
          onChange={(id, name) => {
            onChange({
              ...value,
              parentContactId: name,
              parentContactIdId: id || null
            });
          }}
          value={
            value.parentContactIdId
              ? {
                  id: value.parentContactIdId,
                  name: value.parentContactId
                }
              : null
          }
        />
        <EditDynamicSelectField
          fieldName="relationship_priority"
          label="Relationship Priority"
          model="res.partner"
          onChange={v => {
            updateField('relationshipPriority', v);
          }}
          value={value.relationshipPriority}
        />
        <EditDynamicSelectField
          fieldName="relationship1"
          label="Relationship 1"
          model="res.partner"
          onChange={v => {
            updateField('relationship1', v);
          }}
          value={value.relationship1}
        />
        <EditDynamicSelectField
          fieldName="relationship2"
          label="Relationship 2"
          model="res.partner"
          onChange={v => {
            updateField('relationship2', v);
          }}
          value={value.relationship2}
        />
        <EditFormField
          label="Relationship Strength"
          onChange={v => {
            updateField('relationshipStrength', v ? parseInt(v) : null);
          }}
          type="number"
          value={value.relationshipStrength?.toString() ?? ''}
        />
        <EditFormField
          label="Relationship Strength Description"
          onChange={v => {
            updateField('relationshipStrengthDescription', v);
          }}
          value={value.relationshipStrengthDescription}
        />
        <EditCheckboxField
          checked={value.serviceProvider}
          label="Service Provider"
          onCheckedChange={v => {
            updateField('serviceProvider', v);
          }}
        />
        <EditDynamicSelectField
          fieldName="service_provider_type"
          label="Service Provider Type"
          model="res.partner"
          onChange={v => {
            updateField('serviceProviderType', v);
          }}
          value={value.serviceProviderType}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, boolean | null | number | string> {
  const changes: Record<string, boolean | null | number | string> = {};

  for (const [uiField, config] of Object.entries(
    RELATIONSHIPS_SERVICE_PROVIDER_FIELDS
  )) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else if (config.isNumber) {
        changes[config.apiField] = currentVal as null | number;
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}
